import { React, useEffect, useState } from 'react';
import PageHeader from 'utility/PageHeader';
import PageBody from 'utility/PageBody';
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Table,
  DatePicker,
  List,
  Modal,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { HttpHandler } from '../../shared/httpHandler';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { CryptoHandler } from '../../shared/cryptoHandler';

const Survey = () => {
  const httpHandler = new HttpHandler();
  const cryptoHandler = new CryptoHandler();

  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [surveyContent, setSurveyContent] = useState({
    title: '',
    content: '',
    answerList: [],
  });
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  // 更新表格資料
  const updateData = (pagination = tableParams.pagination) => {
    const { name } = searchForm.getFieldsValue();
    const { current, pageSize } = pagination;
    setLoading(true);
    httpHandler
      .get(
        `/line-official-mgmt/survey/list?user_name=${name}&page=${current}&page_size=${pageSize}`
      )
      .then((response) => {
        if (response.success) {
          response.data.forEach((item) => {
            // 將市話、手機解密
            if (item.telephone) {
              item.telephone = cryptoHandler.decrypt(item.telephone);
            }
            if (item.cellphone) {
              item.cellphone = cryptoHandler.decrypt(item.cellphone);
            }

            // 將答案放入answerList
            item.answerList = [
              item.name,
              item.telephone,
              item.cellphone,
              item.email,
              item.region,
              item.interest_info,
              item.car_preference,
              item.brand_preference,
            ];
            item.create_time = dayjs(item.create_time).format(
              'YYYY-MM-DD HH:mm:ss'
            );
          });
          setTableData(response.data);
          setTableParams({
            ...tableParams,
            pagination: {
              ...pagination,
              total: response.total,
            },
          });
        } else {
          window.antdNotify('錯誤', response.data.message, 'fail');
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Table 分頁、排序、篩選更動時觸發
  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
    updateData(pagination);
  };

  // 搜尋表單送出
  const onFinish = async () => {
    setTableParams({
      ...tableParams,
      pagination: {
        current: 1,
        pageSize: 10,
      },
    });
    updateData({
      current: 1,
      pageSize: 10,
    });
  };

  const SearchBar = () => {
    return (
      <>
        <Form form={searchForm} name="searchForm" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="填寫人員" name="name">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16} className="button-group">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                htmlType="submit"
                className="button button--search">
                搜尋
              </Button>
            </Col>
          </Row>
        </Form>
      </>
    );
  };
  const DataTable = (props) => {
    let columns = [
      {
        title: '問卷名稱',
        dataIndex: 'survey_name',
      },
      {
        title: '填寫人員',
        dataIndex: 'name',
      },
      {
        title: '問卷內容',
        dataIndex: '',
        render: (row) => {
          return (
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                setModalVisible(true);
                setSurveyContent({
                  title: row.survey_name,
                  content: row.content,
                  answerList: row.answerList,
                });
              }}>
              查看
            </Button>
          );
        },
      },
      {
        title: '填寫時間',
        dataIndex: 'create_time',
      },
    ].filter((item) => !item.hidden);

    return (
      <>
        <Table
          columns={columns}
          rowKey={(row) => row.id}
          dataSource={props.tableData}
          pagination={tableParams.pagination}
          loading={loading}
          bordered="true"
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
        />
      </>
    );
  };

  useEffect(() => {
    // 預設表單值
    searchForm.setFieldsValue({
      name: '',
      sales: '',
    });
    // 進入畫面時執行一次搜尋
    onFinish();
  }, []);

  DataTable.propTypes = {
    tableData: PropTypes.array,
  };

  const section = [
    {
      hasTitle: false,
      title: 'Search',
      children: <SearchBar />,
    },
    {
      hasTitle: false,
      title: 'Data',
      children: <DataTable tableData={tableData} />,
    },
    {
      hasTitle: false,
      title: 'Modal',
      children: (
        <SurveyModal
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          surveyContent={surveyContent}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader functionName={'survey'}></PageHeader>
      <PageBody section={section}></PageBody>
    </div>
  );
};

const SurveyModal = (props) => {
  const { modalVisible, setModalVisible, surveyContent } = props;
  const surveyList = surveyContent.content.split(', ');
  return (
    <Modal
      title="問卷內容"
      open={modalVisible}
      onCancel={() => setModalVisible(false)}
      footer={null}>
      <List
        header={<div>問卷名稱：{surveyContent.title}</div>}
        bordered
        dataSource={surveyList}
        renderItem={(item, index) => (
          <List.Item>
            {item}
            <br />
            答：{surveyContent.answerList[index]}
          </List.Item>
        )}
      />
    </Modal>
  );
};

SurveyModal.propTypes = {
  modalVisible: PropTypes.bool.isRequired,
  setModalVisible: PropTypes.func.isRequired,
  surveyContent: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }).isRequired,
};
export default Survey;
